import { css, Styles, withStyles } from '@recruit-robin/robin-theme/web-styles';

import { af, AF_CONTAINER, applyFlowThemeCss, compactQuery, expandedQuery } from '../theme';

export type ApplyStageStyleProps = {
  brandColor: string;
};

const styles = {
  /**
   * The app root. It *is* the screen: at least a screenful tall, scrolled by the document
   * so the brand bar can pin to the window, and the container-query root every responsive
   * rule keys off — the flow queries this element, never the window.
   */
  viewport: ({ props: { brandColor } }) => css`
    ${applyFlowThemeCss(brandColor)};

    background: ${af.stage};
    color: ${af.ink};
    font-family: ${af.font};
    container-type: inline-size;
    container-name: ${AF_CONTAINER};
    overflow-x: hidden;
    display: flex;
    flex-direction: column;

    /*
     * The document scrolls, not this element — see the af-document-scroll class in
     * index.css, which lifts the app shell's height and overflow lock while the flow is
     * mounted, and keeps the brand bar's sticky stuck to the window rather than to a div.
     *
     * The smallest viewport unit, deliberately: the flow must fill the screen with the
     * toolbars showing, and it is a minimum, so nothing reflows when they collapse and the
     * window grows.
     */
    min-height: 100svh;

    @supports not (min-height: 100svh) {
      min-height: 100vh;
    }

    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    /*
     * Inheritance alone never reaches the text. robin-theme sets Outfit on every element and
     * again inside every Typography variant — ApplyText's and the DatePicker's alike — each at
     * one class, so the class is doubled here to outrank them.
     */
    && * {
      font-family: inherit;
    }

    svg {
      display: block;
    }

    /* Purple, so it can never be confused with selection (ink) or brand (red). */
    :focus-visible {
      outline: 3px solid ${af.focus};
      outline-offset: 2px;
    }

    @media (prefers-reduced-motion: reduce) {
      *,
      *::before,
      *::after {
        animation-duration: 0.001ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.001ms !important;
        scroll-behavior: auto !important;
      }
    }
  `,

  stage: css`
    flex: 1 0 auto;
    display: flex;
    justify-content: center;
    padding: ${af.sp10} ${af.sp6} ${af.sp12};

    ${compactQuery} {
      padding: ${af.sp6} ${af.sp4} ${af.sp8};
    }
  `,

  /**
   * One 560px pane, centred. That width is the content measure — roughly 65ch of body
   * type — not a grid coincidence. It never grows; the surplus becomes side space.
   */
  wrap: css`
    width: 560px;
    max-width: 100%;
    display: flex;
    flex-direction: column;

    ${expandedQuery} {
      /*
       * Centring via auto margins rather than align-items:center, so content taller than
       * the screen scrolls instead of being clipped at the top. The margin sits on
       * whatever leads the wrap — bar or card — never between them.
       */
      & > *:first-of-type {
        margin-top: auto;
      }
    }
  `,

  /**
   * No chrome of its own. Content sits straight on the stage; the pieces that need to
   * lift off it carry their own shadow.
   */
  card: css`
    flex: 1 0 auto;
    display: flex;
    flex-direction: column;
    margin-bottom: ${af.sp8};

    ${compactQuery} {
      /*
       * The primary action rides the bottom of the card, in the thumb zone, and the gap
       * above it flexes with screen height. An auto top margin collapses to zero when the
       * content overflows, so long screens degrade to the CTA simply coming last in the
       * scroll — there is no floating sticky bar anywhere in the flow.
       */
      & > [data-af-cta] {
        margin-top: auto;
      }

      & > *:has(+ [data-af-cta]) {
        margin-bottom: ${af.sp6};
      }
    }

    ${expandedQuery} {
      /* The block stops growing; space collects around the group, never inside it. */
      flex: 0 0 auto;
    }
  `,
} satisfies Styles<ApplyStageStyleProps>;

export const useStyles = withStyles(styles);
